import type { ReactNode } from 'react'

export type SelectableCardGridEmptyState = {
  title?: string
  description?: string
  actionLabel?: string
  onAction?: () => void
}

export type SelectableCardDensity = 'compact' | 'comfortable'

export type SelectableCardPreviewState =
  | 'default'
  | 'selected'
  | 'disabled'
  | 'with-tags'
  | 'with-actions'
  | 'with-meta'

export const SELECTABLE_CARD_PREVIEW_STATES: SelectableCardPreviewState[] = [
  'default',
  'selected',
  'disabled',
  'with-tags',
  'with-actions',
  'with-meta',
]

export const SELECTABLE_CARD_PREVIEW_STATE_LABELS: Record<
  SelectableCardPreviewState,
  string
> = {
  default: 'Default',
  selected: 'Selected',
  disabled: 'Disabled',
  'with-tags': 'With tags',
  'with-actions': 'With actions',
  'with-meta': 'With meta',
}

export const SELECTABLE_CARD_DEMO_TITLE = 'Periodic oral evaluation (B1234)'
export const SELECTABLE_CARD_DEMO_SUBTITLE = 'D0120'
export const SELECTABLE_CARD_DEMO_TAGS = ['D0120', 'D1110', 'D0274']
export const SELECTABLE_CARD_DEMO_META = 'Created on 3/12/2026'

export type SelectableCardProps = {
  title?: string
  subtitle?: string
  /** Small chips under the title (e.g. procedure codes in a bundle). */
  tags?: string[]
  /** Footer-style meta line (e.g. created date). */
  meta?: string
  /**
   * Trailing actions (edit/delete). Stops click from selecting the card.
   * Pass `null` to force-hide even when a preview state would show demos.
   */
  actions?: ReactNode | null
  selected?: boolean
  disabled?: boolean
  /**
   * `compact` ≈ Appointment favourites (short).
   * `comfortable` ≈ Settings bundled cards (taller, tags + meta).
   */
  density?: SelectableCardDensity
  className?: string
  /** Dark surface styling (aligned with SelectionBox / DS dark cards). */
  darkMode?: boolean
  onClick?: () => void
  /** Static visual state for the design-system showcase only. */
  previewState?: SelectableCardPreviewState
}

export type SelectableCardGridProps = {
  children?: ReactNode
  /** CSS grid min column width. Defaults to 288px. */
  minColumnWidth?: number
  /** Optional max height with vertical scroll (Appointment Favourites panel). */
  maxHeight?: number | string
  /** @deprecated Prefer `emptyState.title` or `emptyState.description`. */
  emptyMessage?: string
  /** Empty grid content — uses DS Empty state. */
  emptyState?: SelectableCardGridEmptyState
  className?: string
  darkMode?: boolean
}
